import { createContext, useContext, useEffect, useState } from 'react'
import {
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
  onAuthStateChanged
} from 'firebase/auth'
import { auth, googleProvider } from '../firebase'

const AuthContext = createContext()

const EMAIL_KEY = 'emailForSignIn'

export function useAuth() {
  return useContext(AuthContext)
}

const IS_E2E = !!import.meta.env.VITE_E2E

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(!IS_E2E)
  const isEmailLink = isSignInWithEmailLink(auth, window.location.href)
  const savedEmail = isEmailLink ? window.localStorage.getItem(EMAIL_KEY) : null
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(isEmailLink && !savedEmail)

  const completeEmailSignIn = (email) => {
    return signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        window.localStorage.removeItem(EMAIL_KEY)
        setNeedsEmailConfirm(false)
        window.history.replaceState(null, '', window.location.pathname)
      })
      .catch((error) => {
        console.error('Email link sign-in error:', error)
        throw error
      })
  }

  useEffect(() => {
    if (IS_E2E) return

    if (isEmailLink && savedEmail) {
      completeEmailSignIn(savedEmail)
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user)
      setLoading(false)
    })
    return unsubscribe
  }, [])

  const e2eBypass = () => {
    setUser({
      uid: 'e2e-test-user',
      displayName: 'Test User',
      photoURL: null,
      email: 'test@e2e.local'
    })
  }

  const signInWithGoogle = () => {
    if (IS_E2E) return e2eBypass()
    return signInWithPopup(auth, googleProvider)
  }

  const sendEmailLink = (email) => {
    const actionCodeSettings = {
      url: window.location.href,
      handleCodeInApp: true
    }
    return sendSignInLinkToEmail(auth, email, actionCodeSettings).then(() => {
      window.localStorage.setItem(EMAIL_KEY, email)
    })
  }

  const logout = () => {
    return signOut(auth)
  }

  const value = {
    user,
    loading,
    signInWithGoogle,
    sendEmailLink,
    needsEmailConfirm,
    completeEmailSignIn,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
